import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Loader2, X, ChevronDown, ChevronUp, Download, Eye, EyeOff } from "lucide-react";
import { getFaqs, createFaq, updateFaq, deleteFaq } from "@/lib/api";

const CATEGORIES = [
  { key: "general",      label: "Général",       color: "bg-blue-100 text-blue-700" },
  { key: "proprietaire", label: "Propriétaires", color: "bg-green-100 text-green-700" },
  { key: "voyageur",     label: "Voyageurs",     color: "bg-purple-100 text-purple-700" },
];

const DEFAULT_FAQS = [
  { question: "Qu'est-ce que Happy House Conciergerie ?", reponse: "Happy House Conciergerie est une société spécialisée dans la gestion de locations courte durée à Limoges et ses alentours. Nous prenons en charge intégralement votre bien : publication des annonces, accueil des voyageurs, ménage, linge, états des lieux et maintenance. Vous percevez des revenus sans vous occuper de quoi que ce soit.", ordre: 1, categorie: "general" },
  { question: "Quelle est la différence entre l'Offre Sérénité et l'Offre Partenariat ?", reponse: "L'Offre Sérénité repose sur la sous-location professionnelle : nous vous versons un loyer fixe garanti chaque mois. Zéro risque, zéro gestion. L'Offre Partenariat fonctionne sur commission (20 %) : vos revenus varient selon la saisonnalité mais sont généralement plus élevés sur l'année.", ordre: 2, categorie: "general" },
  { question: "Dans quelles zones géographiques intervenez-vous ?", reponse: "Nous intervenons principalement à Limoges et dans un rayon de 50 km, notamment vers Évaux-les-Bains, Saint-Yrieix-la-Perche, Guéret et Brive-la-Gaillarde.", ordre: 3, categorie: "general" },
  { question: "Qu'est-ce que la sous-location professionnelle ?", reponse: "La sous-location professionnelle est un cadre légal qui permet à Happy House de louer votre bien à des voyageurs en votre nom, avec votre accord formalisé par contrat. Vous conservez la pleine propriété et bénéficiez d'un loyer garanti chaque mois.", ordre: 4, categorie: "proprietaire" },
  { question: "Comment sont fixés les prix des nuitées ?", reponse: "Nos experts analysent en continu le marché local, la saisonnalité et les événements à Limoges. Nous appliquons une tarification dynamique qui ajuste vos prix chaque jour pour maximiser le taux d'occupation et le revenu par nuitée.", ordre: 5, categorie: "proprietaire" },
  { question: "Quelles garanties pour mon bien immobilier ?", reponse: "Nous réalisons des états des lieux photographiques avant chaque arrivée et après chaque départ. En cas de dégradation, nous gérons les réclamations et les réparations via notre réseau d'artisans.", ordre: 6, categorie: "proprietaire" },
  { question: "Combien de temps dure le contrat ?", reponse: "Le contrat est signé pour une durée minimale de 12 mois, renouvelable tacitement. Résiliation possible avec un préavis de 3 mois par lettre recommandée.", ordre: 7, categorie: "proprietaire" },
  { question: "Puis-je récupérer mon logement pour un usage personnel ?", reponse: "Oui, dans le cadre de l'Offre Partenariat, vous pouvez bloquer des périodes pour votre usage personnel en nous prévenant à l'avance (4 semaines minimum en haute saison).", ordre: 8, categorie: "proprietaire" },
  { question: "Mon bien doit-il être meublé ?", reponse: "Oui, votre logement doit être meublé et équipé pour la location courte durée (literie, linge, cuisine équipée, WiFi). Si votre bien nécessite des améliorations, nous pouvons vous accompagner dans son aménagement.", ordre: 9, categorie: "proprietaire" },
  { question: "Comment réserver un logement Happy House ?", reponse: "Vous pouvez réserver sur notre site, nous appeler au +33 7 67 48 43 13 ou écrire à contact@happyhouse-conciergerie.fr. Nous confirmons votre réservation sous 24h.", ordre: 10, categorie: "voyageur" },
  { question: "Quels sont les horaires d'arrivée et de départ ?", reponse: "L'arrivée est possible à partir de 16h et le départ doit se faire avant 11h. Des horaires flexibles peuvent être arrangés selon les disponibilités.", ordre: 11, categorie: "voyageur" },
  { question: "Les draps et serviettes sont-ils fournis ?", reponse: "Oui, tous nos logements sont équipés de linge de maison de qualité hôtelière. Le linge est lavé et repassé entre chaque séjour.", ordre: 12, categorie: "voyageur" },
  { question: "Quelle est la fiscalité des revenus de location courte durée ?", reponse: "Les revenus sont imposables dans la catégorie des BIC. Selon votre situation, vous relevez du régime micro-BIC (abattement 50 %) ou du régime réel. Happy House vous fournit un récapitulatif annuel.", ordre: 13, categorie: "proprietaire" },
];

export const FaqCMS = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [filterCat, setFilterCat] = useState("all");
  const [formData, setFormData] = useState({ question: "", reponse: "", ordre: 1, actif: true, categorie: "general" });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const res = await getFaqs();
      setFaqs(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await updateFaq(editing.id, formData);
        toast.success("Question mise à jour !");
      } else {
        await createFaq({ ...formData, ordre: formData.ordre || faqs.length + 1 });
        toast.success("Question ajoutée !");
      }
      setShowModal(false);
      resetForm();
      loadData();
    } catch {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const handleEdit = (faq) => {
    setEditing(faq);
    setFormData({
      question: faq.question,
      reponse: faq.reponse,
      ordre: faq.ordre,
      actif: faq.actif !== false,
      categorie: faq.categorie || "general",
    });
    setShowModal(true);
  };

  const handleToggleActif = async (faq) => {
    try {
      await updateFaq(faq.id, { ...faq, actif: !faq.actif });
      toast.success(faq.actif ? "Question masquée" : "Question publiée");
      loadData();
    } catch {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    try {
      await deleteFaq(id);
      toast.success("Question supprimée !");
      loadData();
    } catch {
      toast.error("Erreur");
    }
  };

  const handleImportDefaults = async () => {
    if (!window.confirm(`Importer ${DEFAULT_FAQS.length} questions par défaut ?`)) return;
    setImporting(true);
    let ok = 0;
    for (const faq of DEFAULT_FAQS) {
      const exists = faqs.some(f => f.question.trim().toLowerCase() === faq.question.trim().toLowerCase());
      if (!exists) {
        try { await createFaq({ ...faq, actif: true }); ok++; } catch {}
      }
    }
    toast.success(`${ok} question(s) importée(s) !`);
    setImporting(false);
    loadData();
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({ question: "", reponse: "", ordre: faqs.length + 1, actif: true, categorie: "general" });
  };

  const openNew = () => {
    resetForm();
    setShowModal(true);
  };

  const getCatStyle = (key) => CATEGORIES.find(c => c.key === key)?.color || "bg-gray-100 text-gray-500";
  const getCatLabel = (key) => CATEGORIES.find(c => c.key === key)?.label || key;

  const filtered = filterCat === "all" ? faqs : faqs.filter(f => (f.categorie || "general") === filterCat);

  if (loading) {
    return <div className="flex items-center justify-center h-32"><Loader2 className="w-6 h-6 animate-spin text-[#004333]" /></div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
        <p className="text-sm text-gray-500">{faqs.length} question{faqs.length !== 1 ? "s" : ""}</p>
        <div className="flex items-center gap-2">
          <button
            onClick={handleImportDefaults}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2 text-sm border border-[#004333] text-[#004333] rounded-lg hover:bg-[#004333]/5 transition-colors disabled:opacity-50"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            Importer par défaut
          </button>
          <button onClick={openNew} className="btn-primary flex items-center gap-2">
            <Plus className="w-4 h-4" /> Nouvelle question
          </button>
        </div>
      </div>

      {/* Category filter tabs */}
      {faqs.length > 0 && (
        <div className="flex gap-2 mb-5 flex-wrap">
          <button
            onClick={() => setFilterCat("all")}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filterCat === "all" ? "bg-[#004333] text-white" : "bg-white border border-gray-200 text-gray-500 hover:border-[#004333]/30"}`}
          >
            Toutes ({faqs.length})
          </button>
          {CATEGORIES.map(cat => {
            const count = faqs.filter(f => (f.categorie || "general") === cat.key).length;
            return (
              <button
                key={cat.key}
                onClick={() => setFilterCat(cat.key)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${filterCat === cat.key ? "bg-[#004333] text-white" : "bg-white border border-gray-200 text-gray-500 hover:border-[#004333]/30"}`}
              >
                {cat.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* FAQ list */}
      {filtered.length > 0 ? (
        <div className="space-y-2">
          {filtered.map((faq) => (
            <div key={faq.id} className={`bg-white border rounded-xl overflow-hidden ${faq.actif === false ? "border-orange-200 opacity-70" : "border-gray-200"}`}>
              <div className="flex items-center gap-3 p-4">
                <button
                  onClick={() => handleToggleActif(faq)}
                  title={faq.actif !== false ? "Masquer" : "Publier"}
                  className={`flex-shrink-0 p-1.5 rounded-lg transition-colors ${faq.actif !== false ? "text-green-500 hover:bg-green-50" : "text-orange-400 hover:bg-orange-50"}`}
                >
                  {faq.actif !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>

                <button
                  onClick={() => setExpanded(expanded === faq.id ? null : faq.id)}
                  className="flex-1 flex items-center gap-3 text-left group min-w-0"
                >
                  <span className={`flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full ${getCatStyle(faq.categorie || "general")}`}>
                    {getCatLabel(faq.categorie || "general")}
                  </span>
                  <span className="font-medium text-[#004333] group-hover:text-[#C9922A] transition-colors leading-snug truncate">
                    {faq.question}
                  </span>
                  {expanded === faq.id ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 ml-auto" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-auto" />}
                </button>

                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => handleEdit(faq)} className="p-1.5 text-gray-400 hover:text-[#004333] rounded-lg hover:bg-gray-100 transition-colors"><Edit className="w-4 h-4" /></button>
                  <button onClick={() => handleDelete(faq.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              {expanded === faq.id && (
                <div className="px-4 pb-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600 leading-relaxed pt-3">{faq.reponse}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white border border-dashed border-gray-300 rounded-xl p-12 text-center">
          <p className="text-gray-400 mb-5">
            {faqs.length === 0 ? "Aucune question. Importez les questions par défaut ou créez-en une." : `Aucune question dans la catégorie "${getCatLabel(filterCat)}".`}
          </p>
          {faqs.length === 0 && (
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <button onClick={handleImportDefaults} disabled={importing}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#004333] text-white rounded-lg text-sm font-medium hover:bg-[#005544] transition-colors disabled:opacity-50">
                {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Importer les {DEFAULT_FAQS.length} questions par défaut
              </button>
              <button onClick={openNew}
                className="flex items-center gap-2 px-5 py-2.5 border border-[#004333] text-[#004333] rounded-lg text-sm font-medium hover:bg-[#004333]/5 transition-colors">
                <Plus className="w-4 h-4" /> Créer manuellement
              </button>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 bg-[#004333]">
              <h2 className="text-white font-semibold">{editing ? "Modifier la question" : "Nouvelle question"}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 text-white/60 hover:text-white rounded-lg hover:bg-white/10 transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Catégorie</label>
                <div className="flex gap-2 flex-wrap">
                  {CATEGORIES.map(cat => (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => setFormData(f => ({ ...f, categorie: cat.key }))}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                        formData.categorie === cat.key
                          ? "bg-[#004333] text-white border-[#004333]"
                          : "bg-white text-gray-500 border-gray-200 hover:border-[#004333]/40"
                      }`}
                    >
                      {cat.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Question <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  required
                  autoFocus
                  value={formData.question}
                  onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                  placeholder="Ex : Comment réserver un logement ?"
                  className="input-hh"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Réponse <span className="text-red-500">*</span></label>
                <textarea
                  rows={6}
                  required
                  value={formData.reponse}
                  onChange={(e) => setFormData({ ...formData, reponse: e.target.value })}
                  placeholder="Rédigez une réponse claire et complète..."
                  className="input-hh resize-none"
                />
                <p className="mt-1 text-xs text-gray-400">{formData.reponse.length} caractères</p>
              </div>

              <div className="flex items-center gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Ordre</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.ordre}
                    onChange={(e) => setFormData({ ...formData, ordre: parseInt(e.target.value) || 1 })}
                    className="input-hh w-24"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Visibilité</label>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <button type="button" onClick={() => setFormData(f => ({ ...f, actif: !f.actif }))}
                      className={`relative w-10 h-5 rounded-full transition-colors ${formData.actif ? "bg-green-500" : "bg-gray-300"}`}>
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.actif ? "translate-x-5" : ""}`} />
                    </button>
                    <span className={`text-sm ${formData.actif ? "text-green-600 font-medium" : "text-gray-400"}`}>
                      {formData.actif ? "Visible" : "Masquée"}
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Annuler</button>
                <button type="submit" className="btn-primary flex-1">{editing ? "Mettre à jour" : "Ajouter"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FaqCMS;
